/**
 * TooltipManager
 *
 * Manages tooltips for form inputs by dynamically adding tooltip icons and handling their behavior.
 * Tooltips provide helpful context to users about why certain information is being collected.
 *
 * The manager supports tooltips for:
 * - Phone inputs
 * - ZIP code inputs
 * - Email inputs
 *
 * Excludes CCPA form fields from having tooltips added.
 *
 * @example
 * const tooltipConfig = {
 *   Phone: {
 *     message: 'We request your phone number to contact you about your inquiry'
 *   },
 *   ZipCode: {
 *     message: 'Your ZIP code helps us find local resources for you'
 *   },
 *   Email: {
 *     message: 'Your email helps us to contact you about your inquiry'
 *   }
 * };
 * const tooltipManager = new TooltipManager(tooltipConfig);
 */


class TooltipManager {
    constructor(options) {
        this.options = options;
        this.init();
    }

    init() {
        Object.keys(this.options).forEach((inputType) => {
            const inputs = this[`get${inputType}Inputs`]();
            inputs.forEach((input) => {
                if (input.dataset.notooltip) {
                    return;
                }
                const label = this.findLabel(input);
                if (label && !label.querySelector('.c-tooltip-icon')) {
                    this.addTooltipIcon(label, inputType);
                    this.setupTooltipBehavior(label, input);
                }
            });
        });
    }

    getPhoneInputs() {
        const excludedIds = ['ccpa_phone'];
        return Array.from(document.querySelectorAll(
            'input[type="tel"], input#phone, input[name="phone"]'
        )).filter((input) => !excludedIds.includes(input.id));
    }

    getZipCodeInputs() {
        const excludedIds = ['ccpa_zip_code'];
        return Array.from(document.querySelectorAll(
            'input#zip_code, input[name="zip_code"]'
        )).filter((input) => !excludedIds.includes(input.id));
    }

    getEmailInputs() {
        const excludedIds = ['ccpa_email'];
        return Array.from(document.querySelectorAll(
            'input[type="email"], input#email, input[name="email"]'
        )).filter((input) => !excludedIds.includes(input.id));
    }

    findLabel(input) {
        return document.querySelector(`label[for="${input.id}"]`) || input.closest('label');
    }

    addTooltipIcon(label, inputType) {
        const icon = document.createElement('span');
        icon.classList.add('c-tooltip-icon');
        icon.setAttribute('role', 'button');
        icon.setAttribute('aria-label', 'Show tooltip');
        icon.addEventListener('click', (e) => {
            e.preventDefault();
            this.addClickTooltip(label, inputType)
        });
        label.appendChild(icon);
    }

    setupTooltipBehavior(label, inputElement) {

        label.addEventListener('mouseleave', () => this.removeTooltip(label));

        inputElement.addEventListener('focus', () => {
            const icon = label.querySelector('.c-tooltip-icon');
            icon.classList.add('focus-input');
        });
        inputElement.addEventListener('blur', () => {
            const icon = label.querySelector('.c-tooltip-icon');
            icon.classList.remove('focus-input');
        });
    }

    addClickTooltip(label, inputType) {
        const existingTooltip = label.querySelector('.c-tooltip');
        if (!existingTooltip) {
            const tooltip = this.createTooltip(label, inputType);
            label.style.position = 'relative';
            label.appendChild(tooltip);
        }
    }

    removeTooltip(label) {
        const existingTooltip = label.querySelector('.c-tooltip');
        if (existingTooltip) {
            existingTooltip.remove();

            const icon = label.querySelector('.c-tooltip-icon');
            if (icon.classList.contains('active')) {
                icon.classList.remove('active');
            }
        }
    }

    createTooltip(label, inputType) {
        const tooltip = document.createElement('div');
        tooltip.classList.add('c-tooltip');
        tooltip.textContent = this.options[inputType].message;

        const triangle = document.createElement('div');
        triangle.classList.add('c-tooltip-arrow');
        tooltip.appendChild(triangle);

        const icon = label.querySelector('.c-tooltip-icon');
        icon.classList.add('active');

        return tooltip;
    }
}

document.addEventListener("DOMContentLoaded", () => {

    const pageNutritionGuide = document.body.classList.contains('page-id-95874');

    let tooltipConfig = {
        Phone: {
            message: "Sharing your phone number ensures we can reach you for follow-up questions or assistance. We’ll only use your phone number for essential communications related to your inquiry."
        }
    };

    if(pageNutritionGuide) {
        tooltipConfig = {
            Phone: {
                message: "Sharing your phone number ensures we can reach you to provide tailored information and assistance."
            }
        };
    }

    new TooltipManager(tooltipConfig);


});
